import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Grid, Link, List, ListItem, ListItemText, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "students_with_targets", label: "Students with Targets", Icon: PeopleIcon,  iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "top_school_name",       label: "Top School Choice",     Icon: SchoolIcon,  iconBg: "#fff7ed", iconColor: "#ea580c", isText: true },
];

const AVATAR_COLORS = ["#7c3aed", "#2563eb", "#16a34a", "#d97706", "#dc2626", "#0891b2", "#9333ea", "#0284c7"];

// Keys are schools.gender's stored values (App\Models\School::GENDERS).
const INTAKE_LABELS = { boys: "Boys", girls: "Girls", "co-ed": "Mixed" };
const INTAKE_COLORS = {
  boys: { bg: "#dbeafe", fg: "#1d4ed8" },
  girls: { bg: "#fce7f3", fg: "#be185d" },
  "co-ed": { bg: "#dcfce7", fg: "#15803d" },
};

/** "12 girls · 5 mixed" — the intake spread behind the top-school tile. */
function intakeMixText(mix) {
  const parts = Object.entries(mix || {})
    .filter(([, count]) => count > 0)
    .map(([g, count]) => `${count} ${(INTAKE_LABELS[g] || g).toLowerCase()}`);
  return parts.length ? parts.join(" · ") : null;
}

function KpiTile({ label, value, subtext, Icon, iconBg, iconColor, isText }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {isText ? (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value || "—"}</Typography>
            {subtext && <Typography variant="caption" color="text.secondary">{subtext}</Typography>}
          </>
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
        )}
      </Box>
    </Paper>
  );
}

function StudentCell({ name, index }) {
  const initials = name
    ? name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()
    : "?";
  const bg = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
        {initials}
      </Box>
      <span>{name}</span>
    </Box>
  );
}

export default function TargetSchoolsAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [schoolType, setSchoolType] = useState("");
  const [schoolGender, setSchoolGender] = useState("");
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [topSchools, setTopSchools] = useState([]);
  const [loading, setLoading] = useState(false);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      if (schoolType) params.school_type = schoolType;
      if (schoolGender) params.gender = schoolGender;
      const res = await api.get("admin/analytics/target-schools", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.rows || []);
      setTopSchools(d.top_targeted_schools || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load target schools");
      setSummary({});
      setRows([]);
      setTopSchools([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId, schoolType, schoolGender]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: "student_name",
      label: "Student",
      render: (r, idx) => <StudentCell name={r.student_name} index={idx} />,
    },
    {
      key: "batch_name",
      label: "Group",
      render: (r) => (
        <Chip
          label={r.batch_name || "—"}
          size="small"
          sx={{ bgcolor: "#ede9fe", color: "#7c3aed", fontWeight: 600 }}
        />
      ),
    },
    { key: "current_year", label: "Current Year" },
    {
      key: "targeted_schools",
      label: "Targeted School(s)",
      render: (r) => (Array.isArray(r.targeted_schools) ? r.targeted_schools.join(", ") : "—"),
    },
    {
      key: "intake",
      label: "Intake",
      // Blank rather than a placeholder: a school with no intake recorded yet is waiting on the AI
      // enrichment pass, which is different from a school that has no intake.
      render: (r) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(r.intake || []).map((g) => (
            <Chip
              key={g}
              label={INTAKE_LABELS[g] || g}
              size="small"
              sx={{ bgcolor: INTAKE_COLORS[g]?.bg || "#f1f5f9", color: INTAKE_COLORS[g]?.fg || "#475569", fontWeight: 600 }}
            />
          ))}
        </Stack>
      ),
    },
    { key: "exam_type", label: "Exam Type" },
    { key: "priority_level", label: "Priority", render: (r) => <StatusChip status={r.priority_level} /> },
  ];

  return (
    <AnalyticsPageShell
      title="Students & Targeted Schools"
      subtitle="View students and the schools they are targeting for their future."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
          showSchoolType
          schoolType={schoolType}
          setSchoolType={setSchoolType}
          showSchoolGender
          schoolGender={schoolGender}
          setSchoolGender={setSchoolGender}
        />
      }
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <KpiTile
            label={TILE_CONFIGS[0].label}
            value={summary.students_with_targets}
            Icon={TILE_CONFIGS[0].Icon}
            iconBg={TILE_CONFIGS[0].iconBg}
            iconColor={TILE_CONFIGS[0].iconColor}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KpiTile
            label={TILE_CONFIGS[1].label}
            value={summary.top_school_name}
            subtext={[
              summary.top_school_count ? `${summary.top_school_count} students` : null,
              intakeMixText(summary.intake_mix),
            ].filter(Boolean).join(" · ") || undefined}
            Icon={TILE_CONFIGS[1].Icon}
            iconBg={TILE_CONFIGS[1].iconBg}
            iconColor={TILE_CONFIGS[1].iconColor}
            isText
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Student Target School List</Typography>
          <AnalyticsDataTable columns={columns} rows={rows} loading={loading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...CARD_SX, position: "sticky", top: 16 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Top Targeted Schools</Typography>
            {topSchools.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            ) : (
              <List dense disablePadding>
                {topSchools.slice(0, 5).map((s, i) => (
                  <ListItem key={s.school_id} disablePadding sx={{ py: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "#ede9fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, mr: 1.5, flexShrink: 0 }}>
                      {i + 1}
                    </Box>
                    <ListItemText
                      primary={s.school_name}
                      secondary={`${s.student_count} student(s)`}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Box sx={{ mt: 2 }}>
              {/* MasterForm reads ?entity= on mount, so this opens straight on the Schools tab. */}
              <Link
                component={RouterLink}
                to="/admin/master-forms?entity=schools"
                underline="hover"
                variant="body2"
                sx={{ color: "#7c3aed" }}
              >
                View all schools →
              </Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </AnalyticsPageShell>
  );
}
